import { useContext } from 'react';
import {
  type QueryClient,
  queryOptions,
  useQuery,
  useQueryClient,
  type UseQueryResult,
  useSuspenseQuery,
  type UseSuspenseQueryResult,
} from '@tanstack/react-query';
import { useFlags } from '@repo/poly-hooks/flags';
import {
  identitiesKycQueries,
  KycCurrentStatus,
  KycProcessStatus,
  KycProcessType,
  kycQueries,
  type KycResponse,
} from '@repo/api-kit/queries/due-diligence/kyc';
import { navigationQueries } from '@repo/api-kit/queries/navigation';
import { CurrentOrganizationIdContext } from '@repo/runtime-kit/organizations/contexts/current-organization-id';
import { mapKycLegacyToAcceptance } from '../utils/map-kyc-legacy-to-acceptance';

/** @internal */
export const kycLegacyQueryKeys = {
  all: ['kyc-legacy'] as const,
  item: (organizationId: string) => [...kycLegacyQueryKeys.all, organizationId] as const,
};

const KYC_REQUIRED_FALLBACK_RESPONSE: KycResponse = {
  kycCurrentStatus: KycCurrentStatus.pending,
  activeProcess: {
    type: KycProcessType.initial,
    processStatus: KycProcessStatus.pending_liveness,
    provider: null,
    providerDetails: null,
    signatureRequired: false,
    membershipId: null,
  },
};

/**
 * Query options for the legacy KYC fallback path.
 *
 * Uses `queryClient.ensureQueryData` internally so that already-cached
 * membership and identities KYC data is reused instead of re-fetched.
 *
 * @param organizationId - The organization ID used to scope the membership fetch
 *   and the query cache key. `null` resolves to {@link KYC_REQUIRED_FALLBACK_RESPONSE}.
 * @param queryClient - The TanStack Query client, used to leverage cached sub-queries.
 */
export function kycLegacyQueries(queryClient: QueryClient, organizationId: string | null) {
  return queryOptions({
    queryKey: kycLegacyQueryKeys.item(organizationId ?? ''),
    queryFn: async (): Promise<KycResponse> => {
      if (!organizationId) return KYC_REQUIRED_FALLBACK_RESPONSE;

      const membership = await queryClient.ensureQueryData(
        navigationQueries.getMembershipsMeAggregate(organizationId)
      );

      const identitiesKyc = await queryClient.ensureQueryData(
        identitiesKycQueries.item(membership.identityId)
      );

      return mapKycLegacyToAcceptance({
        kycStatus: identitiesKyc.kycStatus,
        isReKycRequired: identitiesKyc.isReKycRequired,
        isRenewalPoiRequired: identitiesKyc.isRenewalPoiRequired,
        reKycDueDate: identitiesKyc.reKycDueDate,
        kycRequired: Boolean(membership.kycRequired),
        kycSubmitted: Boolean(membership.kycSubmitted),
        personalInfoProvided: Boolean(membership.personalInfoProvided),
        providerName: identitiesKyc.providerName,
      });
    },
  });
}

/**
 * Hook to fetch KYC status information.
 *
 * @param market - Optional market identifier (e.g. the customer's legal country) to identify
 * customers in register flows.
 * Behaviour is controlled by `feature--boolean-single-kyc`:
 *   - **Enabled**: fetches from the new acceptance API (`/kycb-acceptance/v1/persons/me`)
 *   - **Disabled**: falls back to the legacy endpoints via {@link kycLegacyOptions}
 *
 * @example
 * ```tsx
 * const { data: kycInfo, isLoading, error } = useKycInfo('FR');
 *
 * if (isLoading) return <Spinner />;
 * if (error) return <ErrorMessage error={error} />;
 *
 * return <KycStatus status={kycInfo.kycCurrentStatus} />;
 * ```
 */
export function useKycInfo(market?: string): UseQueryResult<KycResponse> {
  const { featureBooleanSingleKyc } = useFlags();
  const organizationId = useContext(CurrentOrganizationIdContext);
  const queryClient = useQueryClient();

  const newApiResult = useQuery({
    ...kycQueries.item({ market }),
    enabled: Boolean(featureBooleanSingleKyc),
  });

  const fallbackResult = useQuery({
    ...kycLegacyQueries(queryClient, organizationId),
    enabled: Boolean(!featureBooleanSingleKyc && !market && organizationId !== null),
  });

  if (featureBooleanSingleKyc) return newApiResult;

  return fallbackResult;
}

/**
 * Suspense variant of {@link useKycInfo}.
 *
 * Must be rendered inside a `<Suspense>` boundary.
 *
 * @param market - Optional market identifier (e.g. the customer's legal country) to identify
 * customers in register flows.
 * Behaviour is controlled by `feature--boolean-single-kyc`:
 *   - **Enabled**: fetches from the new acceptance API (`/kycb-acceptance/v1/persons/me`)
 *   - **Disabled**: falls back to the legacy endpoints via {@link kycLegacyQueries}
 */
export function useSuspenseKycInfo(market?: string): UseSuspenseQueryResult<KycResponse> {
  const { featureBooleanSingleKyc } = useFlags();
  const organizationId = useContext(CurrentOrganizationIdContext);
  const queryClient = useQueryClient();

  if (featureBooleanSingleKyc) {
    return useSuspenseQuery(kycQueries.item({ market }));
  }

  // Register flows are never membership-authenticated, so skip straight to the fallback.
  return useSuspenseQuery(kycLegacyQueries(queryClient, market ? null : organizationId));
}
